import { useEffect, useState } from 'react';

import { usePermission } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { personnelApi } from '../personnelApi';
import { COMPETENCY_LEVEL_LABELS, SKILL_PROFICIENCY_LEVELS } from '../personnelTypes';
import type {
  CompetencyLevel,
  PersonnelCatalogEntry,
  PersonnelSpecialSkill,
} from '../personnelTypes';
import { sampleSpecialSkill } from '../utils/sampleSpecialSkill';

/**
 * Records or corrects one special skill or hobby — CS Form 212, section VIII, item 31.
 *
 * **The form is wider than the section it comes from, deliberately.** Item 31 prints blank lines
 * and asks for nothing but a list of words; a list of words cannot answer the questions an
 * employer reads this section for. The skill itself is still the form's free text — see below
 * for why it is not a picker — and the four fields around it are the product's own.
 *
 * **The skill is a `<TextField>`, not a `<Select allowCustom>`.** The tail is unbounded —
 * origami, conversational Hokkien, forklift operation — so a catalog would be a table nobody
 * could finish seeding and a picker nobody could find anything in. The CATEGORY is the picker,
 * because six rows genuinely is a vocabulary.
 *
 * **Proficiency offers four rungs and never Not rated.** That value belongs to an assessment
 * nobody made, and this level is claimed rather than assessed; the server refuses it, so
 * offering it would be a control that produces a rejection.
 */
export function EditSpecialSkillModal({
  personnelId,
  editing,
  onClose,
  onSaved,
}: {
  personnelId: string;
  /** Null adds one. */
  editing: PersonnelSpecialSkill | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(editing?.name ?? '');
  const [categoryId, setCategoryId] = useState<string | null>(editing?.categoryId ?? null);
  const [proficiencyLevel, setProficiencyLevel] = useState<CompetencyLevel | null>(
    editing?.proficiencyLevel ?? null,
  );
  const [yearsOfExperience, setYearsOfExperience] = useState(
    editing?.yearsOfExperience === null || editing?.yearsOfExperience === undefined
      ? ''
      : String(editing.yearsOfExperience),
  );
  const [remarks, setRemarks] = useState(editing?.remarks ?? '');

  const [categories, setCategories] = useState<PersonnelCatalogEntry[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const canAddCategory = usePermission('personnel.manage-catalogs');

  useEffect(() => {
    const controller = new AbortController();

    personnelApi
      .catalogs(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setCategories(next.specialSkillCategories);
      })
      .catch(() => {
        /*
          No banner and no focus call: a failed catalog load names no field. The picker renders
          empty and says so through its own loading flag; the form cannot be saved without a
          category, which is the honest outcome rather than a rejection the reader cannot act on.
        */
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingCategories(false);
      });

    return () => controller.abort();
  }, []);

  const yearsValue = yearsOfExperience.trim() === '' ? null : Number(yearsOfExperience);
  const yearsIsNumber = yearsValue === null || Number.isInteger(yearsValue);
  const yearsNotNegative = yearsValue === null || yearsValue >= 0;

  const valid =
    name.trim().length > 0 &&
    categoryId !== null &&
    proficiencyLevel !== null &&
    yearsIsNumber &&
    yearsNotNegative;

  const canSave = valid && confirmed && !saving;

  /** Fills every required field, so the primary action enables on the first try. */
  function generate() {
    const sample = sampleSpecialSkill(categories);

    setName(sample.name);

    // Null only where the catalog is empty — there is no id to invent, so the picker is left
    // alone rather than seeded with one the server would refuse.
    if (sample.categoryId) setCategoryId(sample.categoryId);

    setProficiencyLevel(sample.proficiencyLevel);
    setYearsOfExperience(sample.yearsOfExperience);
    setRemarks(sample.remarks);
  }

  /**
   * Writes the catalog row, then selects the id it comes back with.
   *
   * The picker is over a FOREIGN KEY, so committing the typed text — what a bare `allowCustom`
   * does — would put a label where an id belongs. The create is idempotent on the name, so two
   * readers adding "Language" at once get one row and no error.
   *
   * Appended to the loaded list rather than refetching: a refetch would drop whatever the reader
   * has already filled in on this form.
   */
  async function createCategory(raw: string) {
    const trimmed = raw.trim();
    if (!trimmed) return;

    try {
      const created = await personnelApi.addSpecialSkillCategory(trimmed);

      setCategories((current) =>
        current.some((c) => c.id === created.id) ? current : [...current, created],
      );
      setCategoryId(created.id);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not add that category.', 0),
      );
    }
  }

  async function save() {
    if (!canSave || categoryId === null || proficiencyLevel === null) return;

    setSaving(true);
    setError(null);

    const request = {
      name: name.trim(),
      categoryId,
      proficiencyLevel,

      // Null rather than 0 when the box is empty: "nobody recorded it" and "took it up this
      // year" are different facts.
      yearsOfExperience: yearsValue,

      // Empty is ABSENT, not a blank remark — one meaning for the null downstream.
      remarks: remarks.trim() || null,
    };

    try {
      if (editing) await personnelApi.updateSpecialSkill(personnelId, editing.id, request);
      else await personnelApi.addSpecialSkill(personnelId, request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this record.', 0);

      setError(failure);

      /*
        Every server field name kebabs straight onto an input id here — `categoryId` becomes
        `special-skill-category-id` — so no `fieldIds` map is needed. There is no `<RadioGroup>`
        on this form, which is the one control that needs one.
      */
      focusValidationError(failure, { idPrefix: 'special-skill' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit special skill' : 'Add a special skill'}
      size="2xl"
      id="special-skill-modal"
      autogenerate={{ onGenerate: generate, disabled: saving }}
      confirm={{
        id: 'special-skill-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="special-skill-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="special-skill-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          ONE grid for the four fields that flow together — a skill, what kind of thing it is,
          how good they are at it, and for how long. Remarks is NOT one of them: it is a free
          note about everything above it, so it breaks out full width below, which is the
          container break used deliberately rather than avoided.
        */}
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="special-skill-name"
            label="Skill or hobby"
            required
            description="Written as the employee states it — CS Form 212 prints blank lines here. One row per skill: a reassessment corrects the row rather than adding a second."
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={name.trim() ? error?.fieldError('name') : 'Enter the skill or hobby.'}
            disabled={saving}
          />

          {/*
            The category picker. `allowCustom` with `onCreateOption`: an unmatched query offers
            Add "…" and the handler writes the catalog row under its own key before selecting it.
            Withheld from a reader without that key — hide, don't disable — and the sentence about
            adding one goes with it rather than promising an Add row they will never be shown.
          */}
          <Select
            id="special-skill-category-id"
            label="Category"
            required
            loading={loadingCategories}
            description={
              canAddCategory
                ? 'What kind of thing it is. The form classifies this section not at all, so this list is an employer vocabulary — type a value that is not listed to add it.'
                : 'What kind of thing it is. Choose Other where none of the listed kinds fits, and say what it is in Remarks.'
            }
            allowCustom={canAddCategory}
            onCreateOption={canAddCategory ? createCategory : undefined}
            value={categoryId}
            onChange={(next) => setCategoryId(next || null)}
            options={categories.map((c) => ({ value: c.id, label: c.name }))}
            /*
              The catalog is already ordered by the SortOrder the server assigns, and "Other"
              is seeded last on purpose. Re-sorting alphabetically would file it in the middle.
            */
            sort="none"
            error={categoryId ? error?.fieldError('categoryId') : 'Choose a category.'}
            disabled={saving}
          />

          {/*
            Four rungs and never Not rated — that value belongs to an assessment nobody made, and
            this level is claimed rather than assessed. The server refuses it, so offering it
            would be a control that produces a rejection.

            `sort="none"`, because the list is a LADDER: sorted alphabetically it reads Advanced,
            Basic, Expert, Intermediate, which is alphabetical order pretending to be a ranking.
          */}
          <Select
            id="special-skill-proficiency-level"
            label="Proficiency"
            required
            description="How capable they are at it. The same ladder a competency is rated on, so one person's readings can be compared across the record."
            value={proficiencyLevel === null ? null : String(proficiencyLevel)}
            onChange={(next) =>
              setProficiencyLevel(next === '' ? null : (Number(next) as CompetencyLevel))
            }
            options={SKILL_PROFICIENCY_LEVELS.map((level) => ({
              value: String(level),
              label: COMPETENCY_LEVEL_LABELS[level],
            }))}
            sort="none"
            error={
              proficiencyLevel === null
                ? 'Choose how capable they are at it.'
                : error?.fieldError('proficiencyLevel')
            }
            disabled={saving}
          />

          <TextField
            id="special-skill-years-of-experience"
            label="Years of experience"
            type="number"
            min={0}
            step="1"
            /*
              Optional, and the description says why rather than leaving a required-looking field
              blank: a years total nobody recorded is a real state of a 201 file, and storing 0
              for it would claim they took the skill up this year.
            */
            description="How long they have practised it. Leave blank if nobody recorded it — that is not the same as none."
            value={yearsOfExperience}
            onChange={(e) => setYearsOfExperience(e.target.value)}
            error={
              !yearsIsNumber
                ? 'Enter a whole number of years.'
                : !yearsNotNegative
                  ? 'Enter 0 or more years.'
                  : error?.fieldError('yearsOfExperience')
            }
            disabled={saving}
          />
        </div>

        {/*
          FULL WIDTH, outside the grid — the shape every Remarks field in the product has. It is
          the longest value on the form and the one most likely to be a sentence, so half a row
          is the box that truncates while the field beside it needed none of the room.

          Plain text and NOT a rich-text field: it is a sentence a table renders in a cell, which
          is exactly the case the rich-text rule refuses. An editor here would buy a write-time
          sanitizer, a widened column and 450 kB of JavaScript to store one line.
        */}
        <TextField
          id="special-skill-remarks"
          label="Remarks"
          description="Anything worth saying about it — where it was used, what it covers."
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          error={error?.fieldError('remarks')}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
